import ProductionControllers from '../../../services/productionControllers';

/* eslint-disable no-param-reassign, no-shadow */

const state = {
  devices: [],
  isLoading: false,
  assignmentInProgressByDeviceId: {},
  renameInProgressByDeviceId: {},
  connectedProductionControllerDevicesByExternalId: {},
};

const actions = {
  async getProductionControllerDevices({ commit, dispatch }) {
    commit('SET_IS_LOADING', true);

    try {
      const devices = await ProductionControllers.getDevices();
      commit('SET_DEVICES', devices);
      dispatch('checkConnectedProductionControllerDevices');
    } finally {
      commit('SET_IS_LOADING', false);
    }
  },

  checkConnectedProductionControllerDevices({ commit, rootState }) {
    const ownerId = rootState.production.production?.owner;
    if (!ownerId) {
      commit('SET_CONNECTED_PRODUCTION_CONTROLLER_DEVICES', []);
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      $socket.timeout(5000).emit(
        'checkConnectedProductionControllerDevices',
        { ownerId },
        (error, response) => {
          if (error || response?.ok !== true) {
            console.error(
              'Failed to load connected production controller devices:',
              {
                error: error?.message || null,
                reason: response?.reason || null,
                ownerId,
              },
            );

            commit('SET_CONNECTED_PRODUCTION_CONTROLLER_DEVICES', []);
            resolve();
            return;
          }

          commit('SET_CONNECTED_PRODUCTION_CONTROLLER_DEVICES', response.devices || []);
          resolve();
        },
      );
    });
  },

  async updateProductionControllerDeviceAssignment({ dispatch, commit }, { deviceId, productionId }) {
    commit('SET_ASSIGNMENT_IN_PROGRESS', { deviceId, inProgress: true });

    try {
      await ProductionControllers.updateDeviceAssignment(deviceId, productionId);
      await dispatch('getProductionControllerDevices');
    } finally {
      commit('SET_ASSIGNMENT_IN_PROGRESS', { deviceId, inProgress: false });
    }
  },

  async updateProductionControllerDevice({ dispatch, commit }, { deviceId, name }) {
    commit('SET_RENAME_IN_PROGRESS', { deviceId, inProgress: true });

    try {
      await ProductionControllers.updateDevice(deviceId, name);
      await dispatch('getProductionControllerDevices');
    } finally {
      commit('SET_RENAME_IN_PROGRESS', { deviceId, inProgress: false });
    }
  },

  socket_connectedProductionControllerDevices({ commit }, payload) {
    commit('SET_CONNECTED_PRODUCTION_CONTROLLER_DEVICES', payload.devices || []);
  },

  socket_productionControllerDeviceAssignmentChanged({ dispatch }) {
    dispatch('getProductionControllerDevices');
  },
};

const mutations = {
  SET_IS_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },

  SET_DEVICES(state, devices) {
    state.devices = devices || [];
  },

  SET_ASSIGNMENT_IN_PROGRESS(state, { deviceId, inProgress }) {
    state.assignmentInProgressByDeviceId = {
      ...state.assignmentInProgressByDeviceId,
      [deviceId]: inProgress,
    };
  },

  SET_RENAME_IN_PROGRESS(state, { deviceId, inProgress }) {
    state.renameInProgressByDeviceId = {
      ...state.renameInProgressByDeviceId,
      [deviceId]: inProgress,
    };
  },

  SET_CONNECTED_PRODUCTION_CONTROLLER_DEVICES(state, devices) {
    state.connectedProductionControllerDevicesByExternalId = (devices || []).reduce((memo, device) => {
      const externalDeviceId = String(device.externalDeviceId || '').trim();
      if (!externalDeviceId) return memo;

      return {
        ...memo,
        [externalDeviceId]: {
          ...device,
          externalDeviceId,
        },
      };
    }, {});
  },
};

const getters = {
  productionControllerDevices: (state) => state.devices,

  productionControllersForCurrentProduction: (state, getters, rootState) => {
    const currentProductionId = rootState.production.production?.id || rootState.production.production?._id;
    if (!currentProductionId) return [];

    return state.devices.filter((device) => (
      String(device.assignment?.productionId) === String(currentProductionId)
    ));
  },

  otherProductionControllers: (state, getters, rootState) => {
    const currentProductionId = rootState.production.production?.id || rootState.production.production?._id;

    return state.devices.filter((device) => (
      !device.assignment
      || String(device.assignment.productionId) !== String(currentProductionId)
    ));
  },

  isProductionControllerConnected: (state) => (device) => {
    const externalDeviceId = String(device.externalDeviceId || '').trim();

    return Boolean(
      externalDeviceId
      && state.connectedProductionControllerDevicesByExternalId[externalDeviceId],
    );
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
